import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "./Button";
import { EmptyState } from "./EmptyState";
import { clearLocalData } from "../lib/db";

type Props = { children: ReactNode };
type State = { error: Error | null };

function getRecoveryMessage(error: Error) {
  if (error instanceof DOMException && error.name === "QuotaExceededError") {
    return "Your device storage is full. Export existing data or remove older records, then reload the app.";
  }
  if (error instanceof DOMException && error.name === "InvalidStateError") {
    return "The browser blocked a local storage or crypto operation. Reload first, then check private browsing and site storage settings.";
  }
  return "Reload first. Reset local data only if the app remains unusable.";
}

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("ClearScope runtime failure", error, info);
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <main className="min-h-screen bg-surface-muted px-4 py-10">
        <section className="mx-auto max-w-2xl">
          <EmptyState title="ClearScope hit a local runtime error." variant="error">
            {getRecoveryMessage(this.state.error)}
          </EmptyState>
          <pre className="mt-4 max-h-48 overflow-auto rounded-card bg-surface-sunken p-4 text-xs leading-5 text-text-muted">
            {this.state.error.message}
          </pre>
          <div className="mt-5 flex flex-wrap gap-3">
            <Button onClick={() => window.location.reload()}>Reload app</Button>
            <Button
              variant="danger"
              onClick={() => {
                void clearLocalData().then(() => window.location.reload());
              }}
            >
              Reset local data
            </Button>
          </div>
        </section>
      </main>
    );
  }
}
